import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { categories, articles } from '@/mocks/articles';

const featuredArticles = articles.filter((a) => a.featured).slice(0, 3);
const trendingArticles = articles.filter((a) => a.trending);
const latestArticles = articles.slice(0, 6);

export default function Home() {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [ctaEmail, setCtaEmail] = useState('');
  const [ctaStatus, setCtaStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [ctaError, setCtaError] = useState('');

  const handleCtaSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ctaEmail.trim()) return;
    try {
      const response = await fetch('https://readdy.ai/api/form/d9slrp94ne17ei0sdjjg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ email: ctaEmail.trim() }).toString(),
      });
      const responseText = await response.text();
      if (response.ok) {
        setCtaStatus('success');
        setCtaEmail('');
      } else {
        setCtaStatus('error');
        setCtaError(responseText || '订阅失败，请稍后再试');
      }
    } catch {
      setCtaStatus('error');
      setCtaError('网络错误，请稍后再试');
    }
  };

  const filteredCategories = activeCategory
    ? categories.filter((c) => c.id === activeCategory)
    : categories;

  const homeJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '蜜桃网',
    url: 'https://mitaowang.com/',
    description: '蜜桃网是值得信赖的内容聚合与资源发现平台，汇聚海量优质内容，提供便捷高效的一站式浏览体验。',
    inLanguage: 'zh-CN',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://mitaowang.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(homeJsonLd)}
      </script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20warm%20abstract%20gradient%20background%20with%20soft%20peach%20cream%20and%20warm%20beige%20tones%2C%20gentle%20flowing%20geometric%20shapes%2C%20minimal%20elegant%20texture%2C%20light%20airy%20atmosphere%2C%20soft%20diffused%20lighting%2C%20modern%20abstract%20art%20style%20with%20warm%20harmonious%20composition&width=1920&height=800&seq=hero-bg-mitao&orientation=landscape"
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/5 to-background-50"></div>
          </div>
          <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="font-heading text-3xl md:text-5xl text-background-50 mb-6 leading-tight">
                精选内容，品质阅读
              </h1>
              <p className="text-lg md:text-xl text-background-50/80 mb-10 leading-relaxed">
                蜜桃网汇聚海量优质内容，为您提供便捷、高效的一站式浏览体验。
                探索科技前沿、生活美学、文化视野，发现更广阔的世界。
              </p>
              <div className="flex items-center max-w-xl mx-auto bg-background-50 rounded-full overflow-hidden shadow-lg">
                <div className="flex-1 flex items-center px-6">
                  <i className="ri-search-line text-foreground-400 text-lg mr-3"></i>
                  <input
                    type="text"
                    placeholder="搜索您感兴趣的内容..."
                    className="flex-1 py-4 text-sm bg-transparent text-foreground-900 placeholder-foreground-400 focus:outline-none"
                  />
                </div>
                <button className="px-8 py-4 bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">
                  搜索
                </button>
              </div>
              <div className="flex items-center justify-center gap-4 mt-6 flex-wrap">
                {categories.slice(0, 3).map((cat) => (
                  <Link
                    key={cat.id}
                    to={`/category/${cat.slug}`}
                    className="text-sm text-background-50/70 hover:text-background-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    #{cat.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="py-14 md:py-20">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                探索内容分类
              </h2>
              <p className="text-foreground-500 max-w-xl mx-auto">
                无论您对什么领域感兴趣，这里都有专属于您的精选内容
              </p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.slug}`}
                  className="group flex flex-col items-center p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-3 group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                    {cat.name}
                  </span>
                  <span className="text-xs text-foreground-400 mt-1 text-center line-clamp-2">
                    {cat.description}
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                  精选推荐
                </h2>
                <p className="text-foreground-500 text-sm">
                  编辑团队精心挑选的优质内容
                </p>
              </div>
              <Link
                to="/category/keji"
                className="text-sm font-medium text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer flex items-center gap-1"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {featuredArticles.map((article) => (
                <ArticleCard
                  key={article.id}
                  id={article.id}
                  title={article.title}
                  excerpt={article.excerpt}
                  categoryId={article.categoryId}
                  author={article.author}
                  date={article.date}
                  image={article.image}
                  readTime={article.readTime}
                  variant="default"
                />
              ))}
            </div>
          </div>
        </section>

        {/* Trending Now */}
        <section className="py-14 md:py-20">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-8 h-8 rounded-lg bg-accent-100 flex items-center justify-center">
                <i className="ri-fire-line text-accent-600"></i>
              </div>
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900">
                热门内容
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {trendingArticles.slice(0, 4).map((article) => (
                <ArticleCard
                  key={article.id}
                  id={article.id}
                  title={article.title}
                  excerpt={article.excerpt}
                  categoryId={article.categoryId}
                  author={article.author}
                  date={article.date}
                  image={article.image}
                  readTime={article.readTime}
                  variant="horizontal"
                />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Articles */}
        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
                  最新发布
                </h2>
                <p className="text-foreground-500 text-sm">
                  第一时间获取新鲜内容
                </p>
              </div>
              <div className="flex items-center gap-2">
                {filteredCategories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(activeCategory === cat.id ? null : cat.id)}
                    className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all cursor-pointer whitespace-nowrap ${
                      activeCategory === cat.id
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-50 text-foreground-500 border border-background-200 hover:border-primary-300 hover:text-primary-600'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles
                .filter((a) => !activeCategory || a.categoryId === activeCategory)
                .map((article) => (
                  <ArticleCard
                    key={article.id}
                    id={article.id}
                    title={article.title}
                    excerpt={article.excerpt}
                    categoryId={article.categoryId}
                    author={article.author}
                    date={article.date}
                    image={article.image}
                    readTime={article.readTime}
                    variant="default"
                  />
                ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-14 md:py-20">
          <div className="w-full max-w-3xl mx-auto px-6 md:px-10 text-center">
            <div className="rounded-2xl bg-background-100 border border-background-200/70 p-10 md:p-14">
              <h2 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-4">
                加入蜜桃网读者社区
              </h2>
              <p className="text-foreground-500 mb-8 max-w-lg mx-auto">
                每周精选内容直达您的邮箱，与数万读者一起探索知识的乐趣
              </p>
              {ctaStatus === 'success' ? (
                <div className="flex items-center justify-center gap-2 text-primary-600 text-sm mt-4">
                  <i className="ri-checkbox-circle-line text-lg"></i>
                  订阅成功！感谢您的关注。
                </div>
              ) : (
                <form onSubmit={handleCtaSubscribe} className="flex flex-col sm:flex-row items-center gap-3 max-w-md mx-auto" data-readdy-form="true">
                  <input
                    type="email"
                    name="email"
                    value={ctaEmail}
                    onChange={(e) => setCtaEmail(e.target.value)}
                    placeholder="请输入您的邮箱"
                    required
                    className="flex-1 w-full px-4 py-3 text-sm rounded-full border border-background-200 bg-background-50 text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300"
                  />
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="honeypot-field"
                  />
                  <button
                    type="submit"
                    className="w-full sm:w-auto px-8 py-3 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    免费订阅
                  </button>
                  {ctaStatus === 'error' && (
                    <p className="text-xs text-red-500 w-full text-center">{ctaError}</p>
                  )}
                </form>
              )}
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}